import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import { Link, Stack, useColorScheme } from '@mui/joy';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import GlobalStyles from '@mui/joy/GlobalStyles';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { isYoungConnectEnabled } from '../../features/young-connect/config/moduleFlag';
import { youngConnectPaths } from '../../features/young-connect/config/paths';
import { useUser } from '../../hooks/useUser';
import { DynamicLogo } from '../layout/navbar/dynamicLogo';
import { closeSidebar } from './utils';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';

export default function Sidebar() {
  const location = useLocation();
  const userQuery = useUser();
  const { mode: _mode, setMode } = useColorScheme();

  const logout = () => {
    window.location.href = '/logout';
  };

  useEffect(() => {
    closeSidebar();
  }, [location]);

  useEffect(() => {
    setMode('light');
  }, [setMode]);

  if (!userQuery.isSuccess) return null;

  const user = userQuery.data;
  const isAdmin = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';

  return (
    <Sheet
      className="Sidebar"
      sx={(theme) => ({
        position: 'fixed',
        transform:
          'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
        [theme.breakpoints.up('lg')]: {
          position: 'fixed',
          transform: 'none',
        },
        transition: 'transform 0.4s, width 0.4s',
        zIndex: 10000,
        height: '100dvh',
        width: 'var(--Sidebar-width)',
        top: 0,
        p: 2,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRight: '1px solid',
        borderColor: 'divider',
      })}
    >
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Sidebar-width': '220px',
            [theme.breakpoints.up('lg')]: {
              '--Sidebar-width': '300px',
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={(theme) => ({
          position: 'fixed',
          zIndex: 9998,
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform:
            'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
          [theme.breakpoints.up('lg')]: {
            transform: 'translateX(-100%)',
          },
        })}
        onClick={() => closeSidebar()}
      />

      {/* Dynamic logo */}
      <Box
        sx={(theme) => ({
          display: 'none',
          [theme.breakpoints.up('lg')]: {
            display: 'flex',
          },
          width: '100%',
          justifyContent: 'center',
        })}
      >
        <Box
          sx={{
            width: '65%',
          }}
        >
          <DynamicLogo />
        </Box>
      </Box>

      <Divider
        sx={(theme) => ({
          display: 'none',
          [theme.breakpoints.up('lg')]: {
            display: 'block',
          },
        })}
      />

      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            '--List-nestedInsetStart': '30px',
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
          }}
        >
          <ListItem>
            <Link to="/" component={RouterLink}>
              <ListItemButton>
                <HomeRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Home</Typography>
                </ListItemContent>
              </ListItemButton>
            </Link>
          </ListItem>

          <ListItem>
            <Link to="/topics" component={RouterLink}>
              <ListItemButton>
                <QuestionAnswerRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Topics</Typography>
                </ListItemContent>
              </ListItemButton>
            </Link>
          </ListItem>

          <ListItem>
            <Link to="/events" component={RouterLink}>
              <ListItemButton>
                <DateRangeRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Events</Typography>
                </ListItemContent>
              </ListItemButton>
            </Link>
          </ListItem>

          <ListItem>
            <Link to="/courses" component={RouterLink}>
              <ListItemButton>
                <SchoolRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">E-learning</Typography>
                </ListItemContent>
              </ListItemButton>
            </Link>
          </ListItem>

          <ListItem>
            <Link to="/library" component={RouterLink}>
              <ListItemButton>
                <Inventory2RoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Bibliotheek</Typography>
                </ListItemContent>
              </ListItemButton>
            </Link>
          </ListItem>

          {isYoungConnectEnabled && (
            <ListItem>
              <Link to={youngConnectPaths.home} component={RouterLink}>
                <ListItemButton>
                  <AutoStoriesRoundedIcon />
                  <ListItemContent>
                    <Typography level="title-sm">Young Connect</Typography>
                  </ListItemContent>
                </ListItemButton>
              </Link>
            </ListItem>
          )}

          {isAdmin && (
            <ListItem>
              <Link to="/admin/users" component={RouterLink}>
                <ListItemButton>
                  <AdminPanelSettingsRoundedIcon />
                  <ListItemContent>
                    <Typography level="title-sm">Admin</Typography>
                  </ListItemContent>
                </ListItemButton>
              </Link>
            </ListItem>
          )}
        </List>
      </Box>
      <Divider />
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Link to="/personalProfile" component={RouterLink}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Avatar variant="outlined" size="sm" src={user.imageUrl} />
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Typography level="title-sm">{`${user.firstName}`}</Typography>
            </Stack>
          </Stack>
        </Link>
        <IconButton
          size="sm"
          variant="outlined"
          color="warning"
          onClick={logout}
        >
          <LogoutRoundedIcon />
        </IconButton>
      </Stack>
    </Sheet>
  );
}
